import { AccountLink } from "@/components/links";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { algod } from "@/lib/algo";
import { calculateBenchState, N, toLocaleString } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { BenchStateFull } from "@myth-finance/bench-sdk";
import { useWallet } from "@txnlab/use-wallet";
import algosdk from "algosdk";
import { cx } from "class-variance-authority";
import { Check, ChevronRight, Hammer, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BlockedCountriesField } from "../blocked-countries-field";
import { TermsAndConditionsField } from "../terms-and-conditions-field";
import { CloseOutButton } from "./close-out-button";
import { DeleteBoxesButton } from "./delete-boxes-button";
import { EditMetaButton, EditMetaFormData } from "./edit-meta-button";
import { EndBenchButton } from "./end-bench-button";
import { WithdrawFeesButton } from "./wihdraw-fees-button";
import { WithdrawButton } from "./withdraw-button";
import { Button } from "@/components/ui/button";

const editMetaSchema = z.object({
  termsAndConditions: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        try {
          new URL(val);
          return true;
        } catch (e) {
          return false;
        }
      },
      {
        message: "Please enter a valid URL",
      }
    ),
  blockedCountries: z.array(z.string()).optional().default([]),
});

export function ManageBench({
  data,
  reloadState,
  className,
}: {
  data: BenchStateFull;
  reloadState: () => any;
  className?: string;
}) {
  const { activeAccount } = useWallet();
  const activeAddress = activeAccount?.address;

  const role =
    data?.ownerAddr === activeAddress
      ? "owner"
      : data?.feeAddr === activeAddress
      ? "fee manager"
      : undefined;
  let benchState = calculateBenchState(data);

  const [boxNames, setBoxNames] = useState<Uint8Array[]>([]);
  const [boxLoadCounter, setBoxLoadCounter] = useState(1);
  const reloadBoxState = () => setBoxLoadCounter((n) => n + 1);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const form = useForm<EditMetaFormData>({
    resolver: zodResolver(editMetaSchema),
    defaultValues: {
      termsAndConditions: data.meta?.tc || "",
      blockedCountries: data.meta?.bc || [],
    },
  });

  const hasChanges = useMemo(() => {
    const currentTc = data.meta?.tc || "";
    const currentBc = data.meta?.bc || [];
    const formTc = form.watch("termsAndConditions") || "";
    const formBc = form.watch("blockedCountries") || [];

    return (
      formTc !== currentTc ||
      JSON.stringify(formBc.sort()) !== JSON.stringify(currentBc.sort())
    );
  }, [
    data.meta?.tc,
    data.meta?.bc,
    form.watch("termsAndConditions"),
    form.watch("blockedCountries"),
  ]);

  const appAddr = useMemo<string>(() => {
    if (!data?.appId) return "";
    return algosdk.getApplicationAddress(data.appId);
  }, [data?.appId]);

  const { data: appAddrAcct, refetch: refetchAlgoAcct } =
    useAlgoAcctInfo(appAddr);
  const appAlgoBalance = useBalance(0, appAddrAcct);
  const appPurchaseBalance = useBalance(data.purchaseAssetId, appAddrAcct);
  const appSellingBalance = useBalance(data.sellingAssetId, appAddrAcct);
  const displayBalances: [number, string, number, number][] = useMemo(
    () => [
      [0, "ALGO", 1e6, appAlgoBalance],
      [
        N(data.purchaseAssetId),
        data.purchaseUnitName,
        N(data.purchaseDecimalDenom),
        N(appPurchaseBalance),
      ],
      [
        N(data.sellingAssetId),
        data.sellingUnitName,
        N(data.sellingDecimalDenom),
        N(appSellingBalance),
      ],
    ],
    [data?.appId]
  );

  useEffect(() => {
    if (!appAddr) return;
    (async () => {
      if (boxLoadCounter > 1) refetchAlgoAcct();
      const { boxes } = await algod
        .getApplicationBoxes(Number(data.appId))
        .do();
      const boxNamesAll = boxes.map(({ name: n }) => n);
      setBoxNames(boxNamesAll);
    })();
  }, [boxLoadCounter, appAddr]);

  const withdrawable = Math.max(
    useBalance(data.purchaseAssetId, appAddrAcct) - data.feesAccrued,
    0
  );

  if (!data.appId) return;
  if (!role) return;

  return (
    <>
      <Card
        className={cx(
          `min-w-[381px] mt-6 mb-4 shadow-sm border-opacity-50 backdrop-blur-sm`,
          className
        )}
      >
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold text-foreground">
                Manage Bench
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground/70">
                You are logged in with the <strong>{role}</strong> address
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size={"icon"} onClick={reloadState}><RefreshCw /></Button>
              <Hammer size={30} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="flex flex-col gap-1 flex-1">
              <div className="font-bold">Bench Balances</div>
              <div className="text-muted-foreground flex flex-col gap-1 mt-1">
                {displayBalances.map(([id, name, denom, balance]) => (
                  <div key={`b${id}`} className="flex text-sm justify-between">
                    <div>
                      {name}:{" "}
                      {balance < 0 ? "-" : toLocaleString(balance / denom)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 min-w-[200px]">
              <div className="flex flex-col gap-1">
                <div className="text-sm font-medium">Sale Progress</div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        data.total > 0
                          ? ((data.total - data.remainingTokens) / data.total) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  {toLocaleString(data.total - data.remainingTokens)} /{" "}
                  {toLocaleString(data.total)} {data.sellingUnitName}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <div className="text-sm font-medium">Time Progress</div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${(() => {
                        const now = Date.now() / 1000;
                        const start = Number(data.timeStart);
                        const end = Number(data.timeEnd);

                        if (start === 0 && end === 0) return 0;
                        if (start === 0) return now >= end ? 100 : 0;
                        if (end === 0) return now >= start ? 100 : 0;
                        if (now < start) return 0;
                        if (now > end) return 100;
                        return ((now - start) / (end - start)) * 100;
                      })()}%`,
                    }}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  {(() => {
                    const start = Number(data.timeStart);
                    const end = Number(data.timeEnd);
                    const now = Date.now() / 1000;
                    if (end && end < start) return "Cancelled";
                    if (start === 0 && end === 0) return "No time limits set";
                    if (start === 0 && end > now)
                      return `Ends ${new Date(
                        end * 1000
                      ).toLocaleDateString()}`;
                    if (start === 0 && end <= now)
                      return `Ended ${new Date(
                        end * 1000
                      ).toLocaleDateString()}`;
                    if (end === 0 && start > now)
                      return `Starts ${new Date(
                        start * 1000
                      ).toLocaleDateString()}`;
                    if (end === 0 && start <= now)
                      return `Started ${new Date(
                        start * 1000
                      ).toLocaleDateString()}`;
                    if (end === 0)
                      return `Started ${new Date(
                        start * 1000
                      ).toLocaleDateString()}`;
                    return `${new Date(
                      start * 1000
                    ).toLocaleDateString()} - ${new Date(
                      end * 1000
                    ).toLocaleDateString()}`;
                  })()}
                </div>
              </div>
            </div>
          </div>

          {benchState === "closed" && appAlgoBalance === 0 ? (
            <>
              <div className="flex-col flex items-end text-green-500">
                <div className="flex items-center">
                  Bench is paid out <Check className="ml-1" />
                </div>
              </div>
            </>
          ) : null}

          {benchState === "open" && appAddr && appAlgoBalance ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex flex-col gap-1">
                <div className="font-bold">Withdraw revenue</div>
                <div className="text-muted-foreground text-sm">
                  Recipient: <AccountLink address={data.ownerAddr} />
                </div>
              </div>
              <div className="flex justify-end">
                <WithdrawButton
                  amount={withdrawable}
                  data={data}
                  reloadState={reloadState}
                />
              </div>
            </div>
          ) : null}

          {benchState === "open" && appAddr && appAlgoBalance ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex flex-col gap-1">
                <div className="font-bold">Withdraw platform fees</div>
                <div className="text-muted-foreground text-sm">
                  Recipient: <AccountLink address={data.feeAddr} />
                </div>
              </div>
              <div className="flex justify-end">
                <WithdrawFeesButton data={data} reloadState={reloadState} />
              </div>
            </div>
          ) : null}

          {benchState !== "closed" ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex flex-col gap-1">
                <div>
                  Bench state is <strong>{benchState}</strong>
                </div>
                <div className="text-sm text-muted-foreground">
                  You can manually end the bench sale early.
                </div>
              </div>
              <div className="flex justify-end">
                <EndBenchButton data={data} reloadState={reloadState} />
              </div>
            </div>
          ) : null}

          {role === "owner" && benchState !== "closed" && (
            <Collapsible
              open={isSettingsOpen}
              onOpenChange={setIsSettingsOpen}
              className="space-y-4"
            >
              <CollapsibleTrigger asChild>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded -m-2 transition-colors">
                  <div className="flex flex-col gap-1">
                    <div className="font-bold flex items-center gap-2">
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${
                          isSettingsOpen ? "rotate-90" : ""
                        }`}
                      />
                      Bench Settings
                    </div>
                    <div className="text-muted-foreground text-sm">
                      Edit T&Cs URL and restricted countries
                    </div>
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-6">
                <Card className="bg-background border-none">
                  <CardContent className="pt-6">
                    <Form {...form}>
                      <form className="space-y-6">
                        <TermsAndConditionsField
                          control={form.control}
                          name="termsAndConditions"
                          edit={true}
                        />

                        {hasChanges && (
                          <div className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                            You have unsaved changes
                          </div>
                        )}

                        <Separator />

                        <BlockedCountriesField
                          control={form.control}
                          name="blockedCountries"
                          showHeader={true}
                        />
                      </form>
                    </Form>

                    <div className="flex items-center justify-between gap-4 mt-4">
                      <div className="flex-1">
                        {hasChanges && (
                          <div className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                            You have unsaved changes
                          </div>
                        )}
                      </div>
                      <EditMetaButton
                        data={data}
                        reloadState={reloadState}
                        onSuccess={() => {
                          reloadState();
                          setIsSettingsOpen(false);
                        }}
                        formData={{
                          termsAndConditions:
                            form.watch("termsAndConditions") || "",
                          blockedCountries:
                            form.watch("blockedCountries") || [],
                        }}
                        disabled={!form.formState.isValid}
                      />
                    </div>
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          )}

          {benchState === "closed" && boxNames?.length ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex flex-col gap-1">
                <div>
                  Bench has <strong>{boxNames?.length} boxes</strong>
                </div>
                <div className="text-muted-foreground text-sm">
                  You must delete boxes before closing out:
                </div>
              </div>
              <div className="flex justify-end">
                <DeleteBoxesButton
                  data={data}
                  boxes={boxNames}
                  reloadState={reloadBoxState}
                />
              </div>
            </div>
          ) : null}

          {boxNames?.length === 0 &&
          benchState === "closed" &&
          appAddr &&
          appAlgoBalance ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex flex-col gap-1">
                <div>
                  <strong>Close out bench</strong>
                </div>
                <div className="text-muted-foreground text-sm">
                  Opt out of all assets and pay out
                </div>
              </div>
              <div className="flex justify-end">
                <CloseOutButton
                  algoBalance={appAlgoBalance}
                  purchaseBalance={appPurchaseBalance}
                  data={data}
                  reloadState={reloadBoxState}
                />
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}
